import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Check, Copy, HatGlasses, GraduationCap, LifeBuoy, UserSearch } from 'lucide-react-native';
import { useState } from 'react';
import type { GestureResponderEvent } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import { ClinicalCaseHeaderLine } from '@/components/chat/ClinicalCaseHeaderLine';
import type { HeaderItem } from '@/components/headerItems/types';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeIcon, BadgeText } from '@/components/ui/badge';
import { Box } from '@/components/ui/box';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import {
  MENTOR_INQUIRY_BADGE_LABEL,
  ANONYMOUS_INQUIRY_BADGE_LABEL,
  NO_EXPERT_INQUIRY_BADGE_LABEL,
  PRIMUM_SUPPORT_MESSAGE_CAPTION,
} from '@/constants/inquiryDisplay';
import { InquiryParticipant } from '@/hooks/useChat';
import useClipboard from '@/hooks/useClipboard';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { getAvatarUrl } from '@/util/getAvatarUrl';

const HEADER_AVATAR_SIZE = {
  height: 36,
  width: 36,
};

export type ChatHeaderProfileProps = {
  /** Lets the header show the case once the opening message scrolls away. */
  conversationId?: string;
  recipient: Pick<InquiryParticipant, 'id' | 'avatar'>;
  displayName: string;
  displaySubject: string;
  onViewProfile: () => void;
  showAnonymousBadge?: boolean;
  showMentorBadge?: boolean;
  showNoExpertBadge?: boolean;
  showSupportBadge?: boolean;
  /** MOD recipient view, single-sibling group: caption under the subject naming
   *  the route ("J&J MSL" / "J&J Medical Information Team"). */
  routeCaption?: string;
  /** MOD recipient view, MSL-routed: postal code rendered after the displayName
   *  with a copy-to-clipboard affordance for J&J Medical Connect lookup. */
  postalCode?: string;
};

/**
 * Inline postal code chip with copy-to-clipboard affordance, matching the
 * Copy → Check pattern used in `FormattedResponseDisplay`. Renders nothing
 * when no postal code is provided.
 *
 * The press handler stops propagation and prevents the default action so it
 * doesn't bubble to the parent header `Pressable` / `RouterLink` (which
 * navigates to the participant's profile). On web `RouterLink` renders an
 * `<a href>`, so `preventDefault` is required to cancel browser navigation;
 * `stopPropagation` covers the React Native press-responder path.
 */
export function PostalCodeWithCopy({
  postalCode,
  size = 'sm',
}: {
  postalCode?: string;
  size?: 'xs' | 'sm';
}) {
  const { copyToClipboard } = useClipboard();

  const [copied, setCopied] = useState(false);

  if (!postalCode) {
    return null;
  }

  const handleCopy = (event: GestureResponderEvent | React.MouseEvent) => {
    event.stopPropagation();

    if ('preventDefault' in event) {
      event.preventDefault();
    }

    Promise.resolve()
      .then(() =>
        copyToClipboard(postalCode, () => {
          setCopied(true);

          setTimeout(() => setCopied(false), 2000);
        })
      )
      .catch(() => undefined);
  };

  return (
    <HStack className="shrink-0 items-center gap-1">
      <Text size={size} className="text-typography-600">{`· ${postalCode}`}</Text>
      {/* Slop sized to the icon this actually renders rather than to the much
          larger button the shared constant assumes, which left the target well
          under the minimum. The box cannot simply grow instead: this is an
          inline chip in the tightest header in the app. Web still gets the bare
          glyph, since react-native-web drops hit slop entirely. */}
      <Pressable
        onPress={handleCopy}
        hitSlop={getAccessibleHitSlop(14)}
        accessibilityLabel={copied ? 'Postal code copied' : 'Copy postal code'}>
        <Icon as={copied ? Check : Copy} size="xs" className="text-typography-500" />
      </Pressable>
    </HStack>
  );
}

/**
 * Text-only badge for the compact single-line header (mobile native overlay
 * space is too tight for an icon here). Shares the info tint with the
 * full-size pills in `ChatWebContentHeader` so the two surfaces read as one
 * family, just at different sizes.
 */
function InlineTextBadge({ label, size = 'sm' }: { label: string; size?: 'sm' | 'md' }) {
  return (
    <Badge
      size={size}
      action="info"
      variant="outline"
      className="shrink-0 gap-1 self-start border-info-700 bg-background-0 px-1.5">
      <BadgeText size={size} className="normal-case text-info-700">
        {label}
      </BadgeText>
    </Badge>
  );
}

function HeaderSubtitle({
  badgeLabels = [],
  displayName,
  displaySubject,
  postalCode,
  size = 'sm',
}: {
  /** Each label independently true gets its own row, stacked above the name
   *  so a second badge never forces the name to wrap or truncate. */
  badgeLabels?: string[];
  displayName: string;
  displaySubject: string;
  postalCode?: string;
  size?: 'sm' | 'md';
}) {
  const showName = displayName.trim() && displayName.trim() !== displaySubject.trim();

  const nameNode = showName ? (
    <Text
      size={size}
      numberOfLines={1}
      isTruncated
      className={size === 'sm' ? 'text-typography-600' : undefined}>
      {displayName}
    </Text>
  ) : null;

  const postalNode = postalCode ? <PostalCodeWithCopy postalCode={postalCode} size="sm" /> : null;

  const nameRow =
    nameNode || postalNode ? (
      <HStack className="min-w-0 items-center gap-2">
        {nameNode}
        {postalNode}
      </HStack>
    ) : null;

  if (badgeLabels.length > 0) {
    return (
      <VStack className="min-w-0 gap-0.5">
        <HStack className="flex-wrap items-center gap-1">
          {badgeLabels.map((label) => (
            <InlineTextBadge key={label} label={label} size={size} />
          ))}
        </HStack>
        {nameRow}
      </VStack>
    );
  }

  return nameRow;
}

/**
 * The chat's title view on web. Native draws the participant as bar items instead, through
 * `getChatHeaderItems`, so this has no avatar badge of its own.
 */
export function ChatHeaderTitle({
  conversationId,
  recipient,
  displayName,
  displaySubject,
  onViewProfile,
  showAnonymousBadge = false,
  showMentorBadge = false,
  showNoExpertBadge = false,
  showSupportBadge = false,
  routeCaption,
  postalCode,
}: ChatHeaderProfileProps) {
  /**
   * Web has no avatar corner to overlay, so badges render as stacked text
   * pills in the subtitle instead. Support still wins outright — it answers
   * the same "who is this thread with" question as anonymous, so the two
   * would contradict each other. Anonymous and mentor answer different
   * questions (identity hidden vs. submission channel) and can both be true,
   * so both get their own row rather than one hiding the other.
   */
  const badgeLabels = showSupportBadge
    ? [PRIMUM_SUPPORT_MESSAGE_CAPTION]
    : [
        ...(showAnonymousBadge ? [ANONYMOUS_INQUIRY_BADGE_LABEL] : []),
        ...(showMentorBadge ? [MENTOR_INQUIRY_BADGE_LABEL] : []),
        ...(showNoExpertBadge ? [NO_EXPERT_INQUIRY_BADGE_LABEL] : []),
      ];

  const a11yLabel = getChatHeaderAccessibilityLabel({
    recipient,
    displayName,
    displaySubject,
    onViewProfile,
    showAnonymousBadge,
    showMentorBadge,
    showNoExpertBadge,
    showSupportBadge,
  });

  return (
    <Pressable
      onPress={onViewProfile}
      className="min-w-0 max-w-full"
      {...getAccessibilityProps({ label: a11yLabel, role: 'button' })}>
      <HStack space="sm" className="min-w-0 max-w-full items-center">
        <Box className="relative">
          <Avatar style={HEADER_AVATAR_SIZE}>
            <AvatarImage source={getAvatarUrl(recipient.avatar, true) || primum_default_avatar} />
          </Avatar>
        </Box>

        <VStack className="mx-2 w-[160px] min-w-0">
          <HeaderSubtitle
            badgeLabels={badgeLabels}
            displayName={displayName}
            displaySubject={displaySubject}
            postalCode={postalCode}
            size="sm"
          />
          <Heading size="h1-sm" numberOfLines={1} className="min-w-0">
            {displaySubject}
          </Heading>
          <ClinicalCaseHeaderLine conversationId={conversationId} />
          {routeCaption ? (
            <Text size="sm" className="text-typography-600" numberOfLines={1} isTruncated>
              {routeCaption}
            </Text>
          ) : null}
        </VStack>
      </HStack>
    </Pressable>
  );
}

type ChatHeaderOverlay = {
  icon: typeof HatGlasses;
  className: string;
};

/**
 * The corner badge on the header avatar, ordered by how much the viewer needs it: hidden
 * identity first, then who is relaying, then which program.
 */
function getAvatarOverlay({
  showAnonymousBadge,
  showSupportBadge,
  showMentorBadge,
  showNoExpertBadge,
}: ChatHeaderProfileProps): ChatHeaderOverlay | null {
  if (showAnonymousBadge) {
    return { icon: HatGlasses, className: 'bg-secondary-700' };
  }

  if (showSupportBadge) {
    return { icon: LifeBuoy, className: 'bg-info-700' };
  }

  if (showMentorBadge) {
    return { icon: GraduationCap, className: 'bg-primary-600' };
  }

  if (showNoExpertBadge) {
    return { icon: UserSearch, className: 'bg-typography-600' };
  }

  return null;
}

function getChatHeaderAccessibilityLabel({
  displayName,
  displaySubject,
  showAnonymousBadge,
  showSupportBadge,
  showMentorBadge,
  showNoExpertBadge,
}: ChatHeaderProfileProps): string {
  const base = `${displayName}, ${displaySubject}`;

  if (showAnonymousBadge) {
    return `${base}, anonymous inquiry`;
  }

  if (showSupportBadge) {
    return `${base}, Primum Support`;
  }

  if (showMentorBadge) {
    return `${base}, ${MENTOR_INQUIRY_BADGE_LABEL}`;
  }

  if (showNoExpertBadge) {
    return `${base}, no expert selected`;
  }

  return base;
}

/**
 * The conversation's participant as native header items beside the back button: their avatar,
 * then their name over the inquiry's subject. On iOS 26 the two share one Liquid Glass
 * capsule, the way a direct message header reads in a chat app. Both open the participant's
 * profile.
 *
 * The name and subject sit in one item, capped in width, because the bar also holds back and
 * the inquiry's "…" menu: as separate items they ran wider than the bar, and iOS dropped them
 * altogether. A long name truncates; the spoken label carries it in full. The
 * postal code an MSL-routed recipient sees rides on the subject line, not beside the name,
 * so the name keeps the whole width.
 *
 * The items are always present and hidden when empty, so the bar keeps the same children as
 * the inquiry loads and changes. A subject that repeats the name, as support and mentor
 * conversations do, is shown once.
 */
export function getChatHeaderItems(props: ChatHeaderProfileProps): HeaderItem[] {
  const { recipient, displayName, displaySubject, onViewProfile, postalCode, routeCaption } = props;

  const accessibility = getAccessibilityProps({
    label: getChatHeaderAccessibilityLabel(props),
    role: 'button',
  });

  const trimmedName = displayName.trim();

  const trimmedSubject = displaySubject.trim();

  const nameLabel = trimmedName || trimmedSubject;

  const subjectLabel = [trimmedSubject === nameLabel ? '' : trimmedSubject, routeCaption]
    .filter(Boolean)
    .join(' · ');

  const overlay = getAvatarOverlay(props);

  return [
    {
      type: 'view',
      key: 'chat-avatar',
      render: () => (
        <Pressable
          onPress={onViewProfile}
          className="-ml-1 h-11 w-10 items-center justify-center"
          hitSlop={getAccessibleHitSlop(44)}
          {...accessibility}>
          {/* The native bar clips an item to its own bounds, so the badge sits inside
              the padding rather than hanging off the avatar's corner. */}
          <Box className="relative py-1 pl-1">
            <Avatar style={HEADER_AVATAR_SIZE}>
              <AvatarImage source={getAvatarUrl(recipient.avatar, true) || primum_default_avatar} />
            </Avatar>
            {overlay ? (
              <Box
                className={twJoin(
                  'absolute bottom-1 right-0 h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-background-0',
                  overlay.className
                )}
                accessibilityElementsHidden
                aria-hidden>
                <Icon as={overlay.icon} size="2xs" className="text-background-0" />
              </Box>
            ) : null}
          </Box>
        </Pressable>
      ),
    },
    {
      type: 'view',
      key: 'chat-participant',
      isHidden: !nameLabel && !subjectLabel,
      render: () => (
        <Pressable
          onPress={onViewProfile}
          className="h-11 flex-row items-center pr-2"
          {...accessibility}>
          <VStack className="max-w-[160px] web:max-w-[240px]">
            <Text
              numberOfLines={1}
              isTruncated
              size="sm"
              className="font-semibold text-typography-900">
              {nameLabel}
            </Text>
            {subjectLabel || postalCode ? (
              <HStack className="items-center gap-1">
                {subjectLabel ? (
                  <Text
                    numberOfLines={1}
                    isTruncated
                    size="xs"
                    className="shrink text-typography-700">
                    {subjectLabel}
                  </Text>
                ) : null}
                <PostalCodeWithCopy postalCode={postalCode} size="xs" />
              </HStack>
            ) : null}
          </VStack>
        </Pressable>
      ),
    },
  ];
}

export function ChatWebContentHeader({
  conversationId,
  recipient,
  displayName,
  displaySubject,
  onViewProfile,
  showAnonymousBadge = false,
  showMentorBadge = false,
  showNoExpertBadge = false,
  showSupportBadge = false,
  routeCaption,
  postalCode,
}: ChatHeaderProfileProps) {
  return (
    <HStack className="items-center justify-between border-b border-outline-100 px-6 py-4">
      <Pressable onPress={onViewProfile}>
        <HStack space="md" className="items-center">
          <Avatar style={{ height: 48, width: 48 }}>
            <AvatarImage source={getAvatarUrl(recipient.avatar, true) || primum_default_avatar} />
          </Avatar>

          <VStack>
            {showAnonymousBadge ? (
              <Badge
                size="md"
                action="info"
                variant="outline"
                className="mb-1.5 gap-1.5 self-start border-info-700 bg-background-0 px-2"
                accessibilityLabel="Anonymous inquiry coordinated by Ask Anonymously">
                <BadgeIcon as={HatGlasses} size="sm" className="text-info-700" />
                <BadgeText size="md" className="normal-case text-info-700">
                  {ANONYMOUS_INQUIRY_BADGE_LABEL}
                </BadgeText>
              </Badge>
            ) : null}
            {showMentorBadge ? (
              <Badge
                size="md"
                action="info"
                variant="outline"
                className="mb-1.5 gap-1.5 self-start border-info-700 bg-background-0 px-2"
                accessibilityLabel="Mentorship inquiry submitted through Ask a Mentor">
                <BadgeIcon as={GraduationCap} size="sm" className="text-info-700" />
                <BadgeText size="md" className="normal-case text-info-700">
                  {MENTOR_INQUIRY_BADGE_LABEL}
                </BadgeText>
              </Badge>
            ) : null}
            {showNoExpertBadge ? (
              <Badge
                size="md"
                action="muted"
                variant="outline"
                className="mb-1.5 gap-1.5 self-start border-outline-300 bg-background-0 px-2"
                accessibilityLabel="Submitted without selecting an expert">
                <BadgeIcon as={UserSearch} size="sm" className="text-typography-700" />
                <BadgeText size="md" className="normal-case text-typography-700">
                  {NO_EXPERT_INQUIRY_BADGE_LABEL}
                </BadgeText>
              </Badge>
            ) : null}
            <HeaderSubtitle
              badgeLabels={showSupportBadge ? [PRIMUM_SUPPORT_MESSAGE_CAPTION] : []}
              displayName={displayName}
              displaySubject={displaySubject}
              postalCode={postalCode}
              size="sm"
            />
            <Heading size="md">{displaySubject}</Heading>
            <ClinicalCaseHeaderLine conversationId={conversationId} />
            {routeCaption ? (
              <Text size="sm" className="text-typography-600" numberOfLines={1} isTruncated>
                {routeCaption}
              </Text>
            ) : null}
          </VStack>
        </HStack>
      </Pressable>
    </HStack>
  );
}
